/**
 * Shared exam-question primitives.
 *
 * Used by GMAT today; reused as-is for GRE, IELTS, PTE, Selectives, VCE,
 * and any future exam modules. Two rules these components enforce:
 *   1. Full content always visible — no truncation, no ellipsis, no clipped
 *      buttons. Long word problems, long answer choices, long URLs, raw LaTeX
 *      and long unbroken strings all wrap correctly.
 *   2. Vertical expansion, never horizontal overflow.
 *
 * Do not add `truncate`, `line-clamp`, `max-h-*`, or `overflow-hidden` to
 * anything that renders question or answer content.
 */
import React from 'react';
import { Check, X } from 'lucide-react';

// Reusable wrap-friendly class string. Use this anywhere exam content is shown.
// `whitespace-pre-wrap` preserves intentional newlines from the question bank.
// `break-words` lets unbreakable strings (URLs, equations) wrap rather than overflow.
// `[overflow-wrap:anywhere]` is the belt-and-braces fallback for content with
// NO natural break points (e.g. long_underscore_separated_identifiers).
export const EXAM_TEXT_CLS = 'whitespace-pre-wrap break-words [overflow-wrap:anywhere] leading-relaxed';

/** Optional passage / context box rendered above the question stem. */
export const QuestionPassage = ({ text }) => {
  if (!text) return null;
  return (
    <div
      className={`bg-[#FAF9F6] border-l-4 border-[#007A7A] rounded-r-md p-4 text-sm text-gray-800 mb-5 ${EXAM_TEXT_CLS}`}
      data-testid="exam-passage"
    >
      {text}
    </div>
  );
};

/** Optional table/chart data block (Data Insights questions, etc). */
export const QuestionTableData = ({ text }) => {
  if (!text) return null;
  return (
    <div
      className={`bg-gray-50 border border-gray-200 rounded-md p-4 mb-5 text-sm font-mono text-gray-800 overflow-x-auto ${EXAM_TEXT_CLS}`}
      data-testid="exam-table-data"
    >
      {text}
    </div>
  );
};

/** Question stem — main prompt text. */
export const QuestionStem = ({ text, testId = 'exam-question-text' }) => (
  <p className={`text-base sm:text-lg text-gray-900 mb-6 ${EXAM_TEXT_CLS}`} data-testid={testId}>
    {text}
  </p>
);

/**
 * Single answer option button.
 *
 * Note `whitespace-normal` on the button itself — without it, browser-default
 * `<button>` styling collapses long content to nowrap and causes the visible
 * "..." truncation that learners reported.
 *
 * Props:
 *   letter     "a"|"b"|"c"|"d"|"e"
 *   text       the option content
 *   selected   user has picked this option (pre-submit highlight)
 *   correct    server says this is the correct answer (post-submit, green)
 *   wrong      user picked this but it was wrong (post-submit, red)
 *   onClick    handler (no-op when locked)
 *   locked     disables interaction (after submit)
 *   testId     forwarded data-testid
 */
export const AnswerOption = ({
  letter, text,
  selected = false, correct = false, wrong = false,
  onClick, locked = false, testId,
}) => {
  const stateClass =
    correct ? 'border-green-500 bg-green-50'
    : wrong ? 'border-red-500 bg-red-50'
    : selected ? 'border-[#007A7A] bg-[#E6F4F4] shadow-sm'
    : 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50';

  const badgeClass =
    correct ? 'bg-green-500 text-white border-green-500'
    : wrong ? 'bg-red-500 text-white border-red-500'
    : selected ? 'bg-[#007A7A] text-white border-[#007A7A]'
    : 'bg-white text-gray-700 border-gray-300';

  return (
    <button
      type="button"
      onClick={locked ? undefined : onClick}
      disabled={locked}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-start gap-3 whitespace-normal ${stateClass} ${locked ? 'cursor-default' : 'cursor-pointer'}`}
      data-testid={testId}
    >
      <span
        className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold uppercase border ${badgeClass}`}
      >
        {letter}
      </span>
      <span className={`flex-1 min-w-0 text-sm sm:text-base text-gray-900 ${EXAM_TEXT_CLS}`}>
        {text}
      </span>
      {correct && <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />}
      {wrong && <X className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />}
    </button>
  );
};

/**
 * Renders a 4- or 5-option list given the standard option_a..e fields.
 * Pass `state` to drive selected/correct/wrong per option.
 */
export const AnswerOptionList = ({
  question,
  selectedLetter = null,
  result = null,           // { correct_answer } if post-submit, else null
  onSelect,
  locked = false,
  testIdPrefix = 'exam-opt',
}) => {
  const opts = [
    ['a', question.option_a], ['b', question.option_b],
    ['c', question.option_c], ['d', question.option_d],
  ];
  if (question.option_e) opts.push(['e', question.option_e]);
  return (
    <div className="space-y-2.5 mb-6">
      {opts.map(([letter, text]) => (
        <AnswerOption
          key={letter}
          letter={letter}
          text={text ?? ''}
          selected={!result && selectedLetter === letter}
          correct={!!result && letter === result.correct_answer}
          wrong={!!result && letter === selectedLetter && !result.is_correct}
          onClick={() => onSelect?.(letter)}
          locked={locked || !!result}
          testId={`${testIdPrefix}-${letter}`}
        />
      ))}
    </div>
  );
};
